import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Phone, ShieldCheck, Sparkles, ArrowRight, MapPin, Search } from "lucide-react";
import { getServiceIcon } from "@/lib/serviceIcons";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function Landing() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/service-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return services.filter((s) => {
      const catOk = activeCat === "All" || s.category === activeCat;
      const qOk = !needle || s.name.toLowerCase().includes(needle) || (s.description || "").toLowerCase().includes(needle);
      return catOk && qOk;
    });
  }, [services, activeCat, q]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="landing" />

      {/* HERO */}
      <section className="pt-32 pb-20 px-6 lg:px-10 max-w-7xl mx-auto" data-testid="hero-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F3F1ED] border border-[#E5E3DB] text-xs font-semibold text-[#1E3F20] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Trusted local pros, on-demand
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-[#1A1A1A]">
              Home services<br />
              that actually <span className="text-[#E06D53]">show up.</span>
            </h1>
            <p className="mt-6 text-lg text-[#4A4A4A] max-w-xl leading-relaxed">
              From a leaky tap to deep cleaning — book a verified partner near
              you in minutes. Pay only when the job is done.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/customer/login"
                className="inline-flex h-12 px-6 rounded-full btn-accent items-center gap-2 font-semibold"
                data-testid="hero-book-service-btn"
              >
                Book a Service <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/partner/login"
                className="inline-flex h-12 px-6 rounded-full border border-[#E5E3DB] items-center gap-2 font-semibold hover:bg-white"
                data-testid="hero-become-partner-btn"
              >
                Become a Partner
              </Link>
              <a
                href="tel:9015334444"
                className="inline-flex h-12 px-5 rounded-full items-center gap-2 text-[#1A1A1A] font-medium hover:text-[#E06D53]"
                data-testid="hero-helpline"
              >
                <Phone className="w-4 h-4" /> 9015334444
              </a>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-display text-3xl text-[#1E3F20]">16+</div>
                <div className="text-xs text-[#4A4A4A] mt-1">Service categories</div>
              </div>
              <div>
                <div className="font-display text-3xl text-[#1E3F20]">2-50km</div>
                <div className="text-xs text-[#4A4A4A] mt-1">Radius matching</div>
              </div>
              <div>
                <div className="font-display text-3xl text-[#1E3F20]">100%</div>
                <div className="text-xs text-[#4A4A4A] mt-1">Verified partners</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-[#E5E3DB] aspect-[4/5]">
              <img
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjBtb2Rlcm58ZW58MHx8fHwxNzgyNzE0MzI3fDA&ixlib=rb-4.1.0&q=85"
                alt="Professional"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90">
                  <ShieldCheck className="w-4 h-4" /> Verified
                </div>
                <div className="font-display text-2xl mt-1">
                  Trained &amp; background-checked
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24" data-testid="services-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#1E3F20] font-semibold">
              What we do
            </div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-[#1A1A1A]">
              Pick a service, get matched.
            </h2>
          </div>
          <Link
            to="/customer/login"
            className="hidden md:inline-flex text-sm font-semibold text-[#1E3F20] hover:text-[#E06D53] items-center gap-1"
            data-testid="services-cta-link"
          >
            Book now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Search + Categories */}
        <div className="mb-8 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search services..."
              className="octa-input pl-11"
              data-testid="landing-services-search"
            />
          </div>
          <div className="flex-1 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                className={`px-4 h-10 rounded-full text-sm font-semibold transition-colors border ${
                  activeCat === c
                    ? "bg-[#1E3F20] text-white border-[#1E3F20]"
                    : "bg-white text-[#1A1A1A] border-[#E5E3DB] hover:border-[#1E3F20]"
                }`}
                data-testid={`landing-category-${c.replace(/\s|&/g, "-").toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {filtered.length === 0 && (
            <div className="md:col-span-12 text-center py-16 text-[#737373]" data-testid="landing-no-results">
              No services match your search.
            </div>
          )}
          {filtered.map((s, i) => {
            const isHero = i % 7 === 0 || i % 7 === 3;
            const Icon = getServiceIcon(s.icon);
            return (
              <Link
                key={s.id}
                to="/customer/login"
                className={`card-octa overflow-hidden ${
                  isHero ? "md:col-span-6" : "md:col-span-3"
                }`}
                data-testid={`service-card-${s.id}`}
              >
                {s.image && (
                  <div className={`relative ${isHero ? "h-56" : "h-40"}`}>
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#1E3F20]" />
                    </div>
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    {!s.image && (
                      <div className="w-8 h-8 rounded-full bg-[#F3F1ED] flex items-center justify-center">
                        <Icon className="w-4 h-4 text-[#1E3F20]" />
                      </div>
                    )}
                    <div className="font-display-medium text-xl text-[#1A1A1A]">
                      {s.name}
                    </div>
                  </div>
                  <div className="text-sm text-[#737373] mt-1">
                    {s.description}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#1A1A1A] text-white py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-display text-4xl md:text-5xl max-w-2xl">
            Three steps. <span className="text-[#E06D53]">Zero hassle.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {[
              {
                t: "Tell us what you need",
                d: "Choose a service and share your pincode and details.",
              },
              {
                t: "We find partners near you",
                d: "Verified local partners get notified instantly.",
              },
              {
                t: "Pick. Get it done. Pay.",
                d: "Choose from responding partners and track the job.",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="border border-white/10 rounded-2xl p-7 hover:border-[#E06D53] transition-colors"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="font-display text-5xl text-[#E06D53]">
                  0{i + 1}
                </div>
                <div className="font-display-medium text-2xl mt-5">{step.t}</div>
                <div className="text-sm text-white/70 mt-2 leading-relaxed">
                  {step.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER STRIP */}
      <section className="py-20 px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-widest text-[#E06D53] font-semibold">
              For Service Pros
            </div>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-[#1A1A1A]">
              Grow your business with OCTA.
            </h2>
            <p className="mt-5 text-[#4A4A4A] max-w-xl">
              Get qualified leads from customers in your pincode. Choose what
              you do, set your radius, and respond on your terms.
            </p>
            <Link
              to="/partner/login"
              className="mt-8 inline-flex h-12 px-6 rounded-full btn-primary-octa items-center gap-2 font-semibold"
              data-testid="partner-cta-btn"
            >
              Join as Partner <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="card-octa p-7">
              <div className="flex items-center gap-2 text-[#1E3F20]">
                <MapPin className="w-4 h-4" />
                <span className="text-xs uppercase tracking-widest font-semibold">
                  Hyperlocal matching
                </span>
              </div>
              <div className="font-display text-2xl mt-3">
                Leads from <span className="text-[#E06D53]">your pincode</span> only
              </div>
              <ul className="mt-5 space-y-3 text-sm text-[#4A4A4A]">
                <li>• Choose services you provide</li>
                <li>• Set service radius (2-50 km)</li>
                <li>• Respond to leads, see customer details</li>
                <li>• Optional KYC (PAN, GST, Trade Licence)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5E3DB] bg-white py-10 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/octa-logo.png" alt="OCTA" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-display text-lg">
              OCTA <span className="text-[#E06D53]">Services</span>
            </span>
          </div>
          <div className="text-sm text-[#737373]">
            Helpline: <a href="tel:9015334444" className="font-semibold text-[#1A1A1A]" data-testid="footer-helpline">9015334444</a>
          </div>
          <div className="flex gap-4 text-sm">
            <Link to="/admin/login" className="text-[#737373] hover:text-[#1A1A1A]" data-testid="footer-admin-link">
              Admin
            </Link>
            <Link to="/partner/login" className="text-[#737373] hover:text-[#1A1A1A]">
              Partners
            </Link>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <WhatsAppButton
        variant="floating"
        text="Hi OCTA, I want to book a service."
        testId="landing-whatsapp-float"
      />
    </div>
  );
}
